<script setup lang="ts">
/**
 * UI-kit component
 * Category: Ratings
 * Name: RatingGauge
 * FIGMA: https://www.figma.com/design/NgI2cVJkWODrgnOBHO3Zed/Strapi-Builder--DS-?node-id=4462-11405
 *
 * The satisfaction-gauge artwork of SectionRatingsGauge (Figma: img_score — deliberately
 * not a Figma component). Per the task it must ship as vector, never a flat raster:
 * the decorative layer (concentric gradient circles, value arc-ring, sparkles) is the
 * committed Figma SVG export; ticks, needle and the centre circle are drawn in code.
 * The artwork keeps the drawn 660:544 aspect, is height-locked to its host plate and
 * centre-cropped when the plate is narrower (Figma FIXED-660 behavior); the centre
 * typography scales with the artwork via container-query units; the Trustpilot star row
 * is 16px (DS default) on mobile and 24px (Figma TrustpilotRating size=lg, absent from
 * the installed DS) from the tablet breakpoint.
 * The host owns the plate size and radius; this component fills it.
 */
// types
import type { RatingGaugeProps } from '../types/interfaces'
// assets
import gaugeDecor from '../images/gauge-decor.svg'
// components
import { TrustpilotRating } from '@brighterly/lib-lumee-ui'

defineOptions({
  name: 'RatingGauge',
})

withDefaults(defineProps<RatingGaugeProps>(), {
  scoreLabel: 'Great',
  caption: 'Satisfaction score',
  rating: 4.5,
})
</script>

<template>
  <figure
    class="rating-gauge"
    role="img"
    :aria-label="`${caption}: ${scoreLabel} — rated ${rating} out of 5`"
  >
    <div
      class="rating-gauge__artwork"
      aria-hidden="true"
    >
      <img
        class="rating-gauge__decor"
        :src="gaugeDecor"
        alt=""
        width="800"
        height="917"
      >
      <svg
        class="rating-gauge__overlay"
        viewBox="0 0 660 544"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g transform="translate(331.5 437)">
          <rect
            class="rating-gauge__tick"
            x="-2.5"
            y="-193"
            width="5"
            height="18"
            rx="2.5"
            transform="rotate(-90)"
          />
          <rect
            class="rating-gauge__tick"
            x="-2.5"
            y="-193"
            width="5"
            height="18"
            rx="2.5"
            transform="rotate(-44)"
          />
          <rect
            class="rating-gauge__tick"
            x="-2.5"
            y="-193"
            width="5"
            height="18"
            rx="2.5"
          />
          <rect
            class="rating-gauge__tick"
            x="-2.5"
            y="-193"
            width="5"
            height="18"
            rx="2.5"
            transform="rotate(44)"
          />
          <path
            class="rating-gauge__needle"
            d="M-9 -170 L-1.75 -253.2 C-1.5 -255.7 1.5 -255.7 1.75 -253.2 L9 -170 Z"
            transform="rotate(91)"
          />
          <circle
            class="rating-gauge__circle"
            r="175"
          />
        </g>
      </svg>
      <div class="rating-gauge__score">
        <p class="rating-gauge__label">
          {{ scoreLabel }}
        </p>
        <p class="rating-gauge__caption">
          {{ caption }}
        </p>
        <span class="rating-gauge__stars">
          <TrustpilotRating :rating="rating" />
        </span>
      </div>
    </div>
  </figure>
</template>

<style lang="scss" scoped src="../styles/_rating-gauge.scss" />
